import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Loader2, ReceiptText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useAccountStore from '../../store/useAccountStore';
import { getAccountStatementTexts, normalizeAccountStatementLanguage } from '../../utils/accountStatementI18n';
import { withChronologicalStatementBalance } from '../../utils/financeLedgerSort';
import LedgerToolbar from '../common/LedgerToolbar';

function getLocale(language) {
  if (language === 'en') return 'en-US';
  if (language === 'de') return 'de-DE';
  return 'tr-TR';
}

function formatCurrency(value, currency = 'TRY', language = 'tr') {
  return new Intl.NumberFormat(getLocale(language), { style: 'currency', currency }).format(Number(value) || 0);
}

const REPORT_CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'];

function AccountStatement({ accountId, currentLanguage = 'tr', showDownloadButton = true, variant = 'account' }) {
  const [statement, setStatement] = useState(null);
  const [reportCurrency, setReportCurrency] = useState('TRY');
  const [pdfLanguage, setPdfLanguage] = useState('tr');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [sortDirection, setSortDirection] = useState('desc');
  const { fetchAccountLedger, downloadAccountLedgerPDF } = useAccountStore();
  const uiLanguage = normalizeAccountStatementLanguage(currentLanguage);
  const text = getAccountStatementTexts(uiLanguage);
  const isPersonnel = variant === 'personnel';

  useEffect(() => {
    if (!accountId) {
      setStatement(null);
      setError('');
      return undefined;
    }

    let active = true;
    const loadStatement = async () => {
      setLoading(true);
      setError('');

      try {
        const report = await fetchAccountLedger(accountId, {
          reportCurrency,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {})
        });
        const activeReport = report?.reportingCurrencyReport || report || null;
        if (active) setStatement(activeReport);
      } catch (err) {
        if (active) {
          setStatement(null);
          setError(err?.response?.data?.message || text.loadError);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadStatement();

    return () => {
      active = false;
    };
  }, [accountId, fetchAccountLedger, reportCurrency, startDate, endDate, text.loadError]);

  const reportSection = useMemo(() => statement?.sections?.[0] || null, [statement]);
  const reportRows = useMemo(() => reportSection?.rows || [], [reportSection]);
  const displayedRows = useMemo(
    () => withChronologicalStatementBalance(reportRows, sortDirection, reportSection?.openingBalance || 0),
    [reportRows, reportSection?.openingBalance, sortDirection]
  );

  const handleDownloadPdf = async () => {
    if (!accountId || downloading) return;

    setDownloading(true);
    try {
      if (typeof downloadAccountLedgerPDF !== 'function') {
        throw new Error('PDF indirme fonksiyonu bulunamadı.');
      }

      const result = await downloadAccountLedgerPDF(accountId, pdfLanguage, {
        reportCurrency,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {})
      });

      if (!result?.success) {
        toast.error(result?.message || text.pdfDownloadError);
      }
    } catch (err) {
      toast.error(err?.message || text.pdfDownloadError);
    } finally {
      setDownloading(false);
    }
  };

  if (!accountId) {
    return (
      <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-slate-400 font-bold">
        {text.noAccountSelected}
      </div>
    );
  }

  const summaryCards = reportSection ? [
    { label: 'Toplam Hak Ediş', value: reportSection.totalCredit, tone: 'text-emerald-600' },
    { label: 'Toplam Ödeme', value: reportSection.totalDebit, tone: 'text-rose-600' },
    { label: 'Net Bakiye', value: reportSection.closingBalance, tone: reportSection.closingBalance < 0 ? 'text-rose-600' : 'text-slate-900' }
  ] : [];

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-slate-50 bg-slate-50/50 px-5 py-4">
        <ReceiptText size={18} className="text-emerald-500" />
        <h3 className="font-black text-slate-800">{isPersonnel ? 'Personel Ekstresi' : text.accountStatementTitle}</h3>
      </div>

      <div className="space-y-6 p-5 sm:p-6">
        {loading && (
          <div className="flex items-center justify-center gap-3 rounded-[2rem] border border-slate-100 bg-slate-50 p-10 text-slate-400 font-bold">
            <Loader2 size={18} className="animate-spin text-blue-600" />
            {text.loading}
          </div>
        )}

        {!loading && error && (
          <div className="rounded-[2rem] border border-rose-100 bg-rose-50 p-8 text-center text-rose-600 font-bold">
            {error}
          </div>
        )}

        {!loading && !error && !reportSection && (
          <div className="rounded-[2rem] border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-slate-400 font-bold">
            {text.noStatementData}
          </div>
        )}

        {!loading && !error && reportSection && (
        <>
        <section className="rounded-[2rem] border border-slate-100 bg-slate-50/50 p-4 md:p-5">
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="rounded-2xl border border-white bg-white p-4 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Raporlama Dövizi</p>
              <div className="relative mt-2">
                <select
                  value={reportCurrency}
                  onChange={(event) => setReportCurrency(event.target.value)}
                  className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 py-1.5 pr-8 text-lg font-black text-slate-900 outline-none"
                >
                  {REPORT_CURRENCIES.map((currency) => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
            </div>
            {summaryCards.map((card) => (
              <div key={card.label} className="rounded-2xl border border-white bg-white p-4 shadow-sm">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{card.label}</p>
                <p className={`mt-2 text-2xl font-black ${card.tone}`}>
                  {formatCurrency(card.value, reportSection.currency, uiLanguage)}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section
          className="overflow-hidden rounded-[2rem] border border-slate-100 bg-white"
          style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
        >
          <div className="flex items-end justify-between gap-4 px-5 pt-4">
            <div>
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-700">
                Hareketler
              </h3>
              <p className="mt-1 text-[10px] font-bold text-slate-400">
                {displayedRows.length} {text.movementCount}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{text.netBalance}</p>
              <p className={`text-lg font-black ${reportSection.closingBalance < 0 ? 'text-rose-600' : reportSection.closingBalance > 0 ? 'text-blue-600' : 'text-slate-700'}`}>
                {formatCurrency(reportSection.closingBalance, reportSection.currency, uiLanguage)}
              </p>
            </div>
          </div>
          <LedgerToolbar
            sortDirection={sortDirection}
            onSortDirectionChange={setSortDirection}
            startDate={startDate}
            onStartDateChange={setStartDate}
            endDate={endDate}
            onEndDateChange={setEndDate}
            pdfLanguage={pdfLanguage}
            onPdfLanguageChange={setPdfLanguage}
            onExport={showDownloadButton ? handleDownloadPdf : undefined}
            exporting={downloading}
            exportLabel={text.downloadPdf}
          />

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                  <th className="p-4">Tarih</th>
                  <th className="p-4">{isPersonnel ? 'Tür / Açıklama' : 'Belge No / Açıklama'}</th>
                  {!isPersonnel && <th className="p-4 text-right">Orijinal Tutar</th>}
                  {!isPersonnel && <th className="p-4 text-right">Çevrim Kuru</th>}
                  {isPersonnel ? (
                    <>
                      <th className="p-4 text-right">Hakediş</th>
                      <th className="p-4 text-right">Ödeme</th>
                    </>
                  ) : (
                    <th className="p-4 text-right">Tutar</th>
                  )}
                  <th className="p-4 text-right">Yürüyen Bakiye</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {displayedRows.length === 0 ? (
                  <tr>
                    <td colSpan={isPersonnel ? '5' : '6'} className="py-16 text-center text-slate-400 font-bold">
                      {text.noSectionTransactions}
                    </td>
                  </tr>
                ) : displayedRows.map((row) => {
                  const sourceLabel = row.source === 'PersonnelExpense'
                    ? 'Hakediş'
                    : row.source === 'Invoice'
                      ? 'Fatura'
                      : row.source === 'Transaction'
                        ? (isPersonnel
                          ? ((row.credit || 0) > 0 ? 'Tahsilat' : 'Ödeme')
                          : 'Hareket')
                        : row.source || '-';
                  const detailLabel = row.description || row.transactionType || row.category || row.type || '-';

                  return (
                    <tr key={row._id || `${row.source}-${row.sourceId || row.documentNo || ''}-${row.date || ''}`} className="text-sm hover:bg-slate-50/80 transition-colors">
                      <td className="p-4 font-bold text-slate-700">
                        {row.date ? new Date(row.date).toLocaleDateString(getLocale(uiLanguage)) : '-'}
                      </td>
                      <td className="p-4">
                        <div className="font-black text-slate-800">{isPersonnel ? detailLabel : (row.documentNo || '-')}</div>
                        {!isPersonnel && (
                          <>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{detailLabel}</div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{sourceLabel}</div>
                          </>
                        )}
                      </td>
                      {!isPersonnel && (
                        <td className="p-4 text-right font-black text-slate-700">
                          {row.originalAmount ? formatCurrency(row.originalAmount, row.originalCurrency || reportSection.currency, uiLanguage) : '-'}
                        </td>
                      )}
                      {!isPersonnel && (
                        <td className="p-4 text-right font-black text-slate-500">
                          {row.conversionRate
                            ? `1 ${row.originalCurrency || reportSection.currency} = ${Number(row.conversionRate).toFixed(4)} ${reportSection.currency}`
                            : '-'}
                        </td>
                      )}
                      {isPersonnel ? (
                        <>
                          <td className="p-4 text-right font-black text-emerald-600">
                            {(row.credit || 0) > 0 ? formatCurrency(row.credit, reportSection.currency, uiLanguage) : '-'}
                          </td>
                          <td className="p-4 text-right font-black text-rose-600">
                            {(row.debit || 0) > 0 ? formatCurrency(row.debit, reportSection.currency, uiLanguage) : '-'}
                          </td>
                        </>
                      ) : (
                        <td className={`p-4 text-right font-black ${(row.debit || 0) > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {formatCurrency((row.debit || 0) - (row.credit || 0), reportSection.currency, uiLanguage)}
                        </td>
                      )}
                      <td className={`p-4 text-right font-black ${Number(row.runningBalance || 0) >= 0 ? 'text-slate-900' : 'text-rose-600'}`}>
                        {formatCurrency(row.runningBalance, reportSection.currency, uiLanguage)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
        </>
        )}
      </div>
    </div>
  );
}

export default AccountStatement;
